import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWatchlist } from '../../context/WatchlistContext';
import SearchOverlay from '../SearchOverlay';
import '../../styles/NavBar.css';

const primaryLinks = [
  { to: '/shorts', label: 'Shorts', isRoute: true },
  { href: '#movies', label: 'Movies' },
  { href: '#tvshows', label: 'TV Shows' },
  { href: '#categories', label: 'Categories' },
];

const otherLinks = [
  { href: '#anime', label: 'Anime' },
  { href: '#indonesian', label: 'Indonesian' },
  { href: '#indian', label: 'Indian' },
  { href: '#nowplaying', label: 'Now Playing' },
  { href: '#trailers', label: 'Trailers' },
  { href: '#performers', label: 'Popular Performers' },
  { href: '#companies', label: 'Top Companies' },
  { to: '/search', label: 'Search', isRoute: true },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { watchlist } = useWatchlist();
  const location = useLocation();

  const isHome = location.pathname === '/';
  const showSolidBg = !isHome || scrolled || isMenuOpen;

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    onScroll();

    window.addEventListener('scroll', onScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const renderLink = (link) => {
    const className = 'navbar-link';

    if (link.isRoute) {
      return (
        <Link
          key={link.label}
          to={link.to}
          className={className}
          onClick={closeMenu}
        >
          {link.label}
        </Link>
      );
    }

    return (
      <a
        key={link.label}
        href={link.href}
        className={className}
        onClick={closeMenu}
      >
        {link.label}
      </a>
    );
  };

  return (
    <>
      <header
        className={[
          'navbar',
          isHome && 'navbar--hero',
          showSolidBg && 'navbar--scrolled',
          isMenuOpen && 'navbar--open',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="navbar__inner">

          {/* LEFT SIDE */}
          <div className="navbar__left">
            <Link
              to="/"
              className="navbar__logo"
              onClick={closeMenu}
            >
              <img
                src="assets/logo2.png"
                alt="Defilms"
                className="logo"
              />
            </Link>

            <nav
              className="navbar__desktop"
              aria-label="Main navigation"
            >
              {primaryLinks.map(renderLink)}

              {/* MY LIST */}
              <Link
                to="/my-list"
                className="navbar-link navbar-link--list"
                onClick={closeMenu}
              >
                My List

                {watchlist.length > 0 && (
                  <span className="nav-badge">
                    {watchlist.length}
                  </span>
                )}
              </Link>

              {/* DONATE */}
              <a
                href="#donate"
                className="navbar-link"
                onClick={closeMenu}
              >
                Donate
              </a>

              {/* OTHERS */}
              <div className="dropdown">
                <button
                  type="button"
                  className="dropbtn"
                >
                  Others{' '}
                  <i className="fas fa-chevron-down"></i>
                </button>

                <div className="dropdown-content">
                  {otherLinks.map(renderLink)}
                </div>
              </div>
            </nav>
          </div>

          {/* RIGHT SIDE */}
          <div className="navbar__right">

            {/* SEARCH */}
            <button
              type="button"
              className="search-trigger"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <i className="fas fa-magnifying-glass"></i>

              <span className="search-trigger-hint">
                Search... (Ctrl+K)
              </span>
            </button>

            {/* MOBILE MENU BUTTON */}
            <button
              type="button"
              className={`navbar__toggle ${
                isMenuOpen
                  ? 'navbar__toggle--active'
                  : ''
              }`}
              onClick={() =>
                setIsMenuOpen((prev) => !prev)
              }
              aria-label={
                isMenuOpen
                  ? 'Close menu'
                  : 'Open menu'
              }
              aria-expanded={isMenuOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

          </div>
        </div>

        {/* MOBILE MENU */}
        <div
          className={`navbar__mobile ${
            isMenuOpen
              ? 'navbar__mobile--open'
              : ''
          }`}
        >
          <nav
            className="navbar__mobile-nav"
            aria-label="Mobile navigation"
          >
            {primaryLinks.map(renderLink)}

            {/* MY LIST */}
            <Link
              to="/my-list"
              className="navbar-link navbar-link--list"
              onClick={closeMenu}
            >
              My List

              {watchlist.length > 0 && (
                <span className="nav-badge">
                  {watchlist.length}
                </span>
              )}
            </Link>

            {/* DONATE */}
            <a
              href="#donate"
              className="navbar-link"
              onClick={closeMenu}
            >
              Donate
            </a>

            <p className="navbar__mobile-label">
              More
            </p>

            {otherLinks.map(renderLink)}
          </nav>
        </div>
      </header>

      {/* MOBILE BACKDROP */}
      {isMenuOpen && (
        <button
          type="button"
          className="navbar__backdrop"
          onClick={closeMenu}
          aria-label="Close menu"
        />
      )}

      {/* SEARCH OVERLAY */}
      <SearchOverlay
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </>
  );
};

export default Navbar;